import React, { useState, useEffect, useRef } from 'react';
import { useI18n } from '../utils/i18n';
import { Ticket, AlertCircle, RefreshCw, Plus, Search } from 'lucide-react';
import apiService from '../services/apiService';
import TicketCard from './ui/TicketCard';
import { ticketPasstZurSuche } from '../utils/suchFilter';
import '../styles/TicketSidebar.css';

const TicketSidebar = ({ onTicketDrop, isLoading, onRefresh, onTicketDragStart, onTicketDragEnd, onCreateTicket, onTicketClick, onTicketPlanned, unassignedOnly = false, hideViewToggle = false, assigneeFilterOptions = null, projectPlanningFilter = null }) => {
  const { t } = useI18n();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [ticketFilterSettings, setTicketFilterSettings] = useState({});
  const [, setSelectedAssigneeTicketFilterSettings] = useState(null);
  const [selectedAssigneeTicketFilterKeywords, setSelectedAssigneeTicketFilterKeywords] = useState('');
  const [searchFilter, setSearchFilter] = useState('');
  const [viewMode, setViewMode] = useState('mine'); // 'mine' | 'pool'
  const hasInitializedViewMode = useRef(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const lastAttentionRefreshAtRef = useRef(0);

  // When provided (Global calendar), use custom assignee filter buttons instead of default view toggle
  const [selectedAssigneeFilter, setSelectedAssigneeFilter] = useState(null);

  const [draggedTicket, setDraggedTicket] = useState(null);
  const dragImageElRef = useRef(null);

  useEffect(() => {
    loadCurrentUser();
    loadUnplannedTickets();
    loadTicketFilterSettings();
  }, []);

  // React to global ticket updates and attention changes similar to dashboard behavior
  useEffect(() => {
    const onTicketsUpdated = async () => {
      try {
        await loadUnplannedTickets();
      } catch (_) {}
    };

    const onAttentionUpdate = async (e) => {
      try {
        const detail = e?.detail || {};
        const attentionTickets = Array.isArray(detail.tickets) ? detail.tickets : [];
        if (attentionTickets.length > 0) {
          // Throttle: avoid request storms when attention updates fire rapidly
          const now = Date.now();
          const last = lastAttentionRefreshAtRef.current || 0;
          if (now - last < 3000) return;
          lastAttentionRefreshAtRef.current = now;

          // Quick local flag bump so UI indicates new activity immediately
          setTickets(prev => {
            const byId = new Map(prev.map(t => [t.id, t]));
            let changed = false;
            attentionTickets.forEach((meta) => {
              const existing = byId.get(meta.id);
              if (existing) {
                const enriched = {
                  ...existing,
                  // Map aggregate attention to sidebar flags used for red corner + sorting
                  hasNewActivity: !!meta.hasNewGlobal || !!existing.hasNewActivity,
                  hasNewActivityForAssignee: !!meta.hasNewForAssignee || !!existing.hasNewActivityForAssignee
                };
                // Shallow compare by JSON is overkill; assume change when flag differs
                if (enriched.hasNewActivity !== existing.hasNewActivity || enriched.hasNewActivityForAssignee !== existing.hasNewActivityForAssignee) {
                  byId.set(meta.id, enriched);
                  changed = true;
                }
              }
            });
            return changed ? Array.from(byId.values()) : prev;
          });
          // Full refresh to include newly appearing tickets and accurate flags from backend
          try { await loadUnplannedTickets(); } catch (_) {}
        }
      } catch (_) {}
    };

    try { window.addEventListener('ticketsUpdated', onTicketsUpdated); } catch (_) {}
    try { window.addEventListener('ticketsAttentionUpdate', onAttentionUpdate); } catch (_) {}
    return () => {
      try { window.removeEventListener('ticketsUpdated', onTicketsUpdated); } catch (_) {}
      try { window.removeEventListener('ticketsAttentionUpdate', onAttentionUpdate); } catch (_) {}
    };
  }, []);

  // Load ticket filter settings once on component mount
  const loadTicketFilterSettings = async () => {
    try {
      const response = await apiService.getTicketFilterSettings();
      if (response.success) {
        setTicketFilterSettings({
          ...response.filterSettings,
          keywords: response.keywords
        });
      }
    } catch (err) {
      console.error('📋 [TICKET SIDEBAR] Error loading ticket filter settings:', err);
    }
  };

  // Initialize view mode from filter settings (only once)
  useEffect(() => {
    if (hasInitializedViewMode.current) return;
    if (ticketFilterSettings && typeof ticketFilterSettings.showOnlyAssignedToMe === 'boolean') {
      setViewMode(ticketFilterSettings.showOnlyAssignedToMe ? 'mine' : 'pool');
      hasInitializedViewMode.current = true;
    }
  }, [ticketFilterSettings]);

  // Initialize/adjust selected assignee filter when options change (Global calendar)
  useEffect(() => {
    if (assigneeFilterOptions && assigneeFilterOptions.length > 0) {
      // Keep current selection if still present; otherwise pick 'unassigned' or first option
      const isCurrentValid = selectedAssigneeFilter && assigneeFilterOptions.some(opt =>
        (opt.type === selectedAssigneeFilter.type) &&
        (opt.type !== 'user' || (opt.email && selectedAssigneeFilter.email && opt.email.toLowerCase() === selectedAssigneeFilter.email.toLowerCase()))
      );
      if (!isCurrentValid) {
        const defaultOption = assigneeFilterOptions.find(o => o.type === 'unassigned') || assigneeFilterOptions[0];
        setSelectedAssigneeFilter(defaultOption);
      }
    } else {
      setSelectedAssigneeFilter(null);
    }
  }, [assigneeFilterOptions]);

  // Load selected assignee's ticket filter settings (Global calendar)
  useEffect(() => {
    const loadSelectedAssigneeFilterSettings = async () => {
      try {
        if (assigneeFilterOptions && selectedAssigneeFilter && selectedAssigneeFilter.type === 'user' && selectedAssigneeFilter.email) {
          const response = await apiService.getTicketFilterSettingsByEmail(selectedAssigneeFilter.email);
          if (response && response.success) {
            setSelectedAssigneeTicketFilterSettings(response.filterSettings || {});
            setSelectedAssigneeTicketFilterKeywords(response.keywords || '');
          } else {
            setSelectedAssigneeTicketFilterSettings(null);
            setSelectedAssigneeTicketFilterKeywords('');
          }
        } else {
          setSelectedAssigneeTicketFilterSettings(null);
          setSelectedAssigneeTicketFilterKeywords('');
        }
      } catch (err) {
        console.error('📋 [TICKET SIDEBAR] Error loading selected assignee ticket filter settings:', err);
        setSelectedAssigneeTicketFilterSettings(null);
        setSelectedAssigneeTicketFilterKeywords('');
      }
    };
    loadSelectedAssigneeFilterSettings();
  }, [assigneeFilterOptions, selectedAssigneeFilter]);

  // Load current user once on component mount
  const loadCurrentUser = async () => {
    try {
      const userResponse = await apiService.getCurrentUser();
      setCurrentUser(userResponse);
      console.log('📋 [TICKET SIDEBAR] Current user loaded:', userResponse.displayName || userResponse.username);
    } catch (err) {
      console.error('📋 [TICKET SIDEBAR] Error loading current user:', err);
    }
  };

  const loadUnplannedTickets = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Load all tickets so we can include overdue-but-not-completed planned tickets in the sidebar
      const response = await apiService.getTickets();
      setTickets(response.tickets || []);
      console.log('📋 [TICKET SIDEBAR] Loaded tickets (including for overdue re-planning):', response.tickets?.length || 0);
    } catch (error) {
      console.error('📋 [TICKET SIDEBAR] Error loading tickets for planning:', error);
      setError(t('global.errorLoad', 'Fehler beim Laden der Kalenderdaten. Bitte versuchen Sie es später erneut.'));
    } finally {
      setLoading(false);
    }
  };

  // Helper: normalize string for comparison
  const normalizeLower = (value) => (value || '').toString().trim().toLowerCase();

  // Helper: extract assignee identifiers
  const getAssigneeLower = (ticket) => normalizeLower(ticket.assignedTo);
  const getCurrentUserIdentifiers = () => {
    const email = typeof currentUser === 'string' ? currentUser : 
      (currentUser?.userPrincipalName || currentUser?.mail || currentUser?.email);
    const displayName = typeof currentUser === 'string' ? null : 
      (currentUser?.displayName || currentUser?.username || null);
    return { emailLower: normalizeLower(email), displayNameLower: normalizeLower(displayName) };
  };

  // Centralized assignment filter logic (respects Global vs. single-user calendar)
  const doesAssignmentMatch = (ticket, params) => {
    const { isUnassigned, isAssignedToCurrentUser } = params;

    // If custom assignee filter options are provided, use them
    if (assigneeFilterOptions && selectedAssigneeFilter) {
      if (selectedAssigneeFilter.type === 'unassigned') {
        return isUnassigned;
      }
      if (selectedAssigneeFilter.type === 'user') {
        const assigneeLower = getAssigneeLower(ticket);
        const selectedEmailLower = normalizeLower(selectedAssigneeFilter.email);
        const selectedNameLower = normalizeLower(selectedAssigneeFilter.label || selectedAssigneeFilter.displayName);
        // Match either by email or by display label/name
        return (selectedEmailLower && selectedEmailLower === assigneeLower) ||
               (selectedNameLower && selectedNameLower === assigneeLower);
      }
      return true;
    }

    // Default behavior (single calendar): unassignedOnly or viewMode
    if (unassignedOnly) {
      return isUnassigned;
    }

    if (viewMode === 'mine') {
      return isAssignedToCurrentUser;
    }

    if (viewMode === 'pool') {
      return isUnassigned;
    }

    return isAssignedToCurrentUser;
  };

  const handleRefresh = async () => {
    console.log('📋 [TICKET SIDEBAR] Manual refresh triggered');
    await loadUnplannedTickets();
    if (onRefresh) {
      onRefresh();
    }
  };

  // Dynamic ticket removal function for immediate UI feedback
  const removeTicketFromList = (ticketId) => {
    setTickets(prevTickets => {
      const filteredTickets = prevTickets.filter(t => t.id !== ticketId);
      console.log('📋 [TICKET SIDEBAR] Dynamically removed ticket from list:', ticketId);
      console.log('📋 [TICKET SIDEBAR] Remaining tickets:', filteredTickets.length);
      return filteredTickets;
    });
  };

  // Dynamic ticket addition function for reverse operation (unplanning)
  const addTicketToList = async (ticketId) => {
    try {
      // Reload the ticket data to get current state
      const response = await apiService.getTickets();
      const ticket = response.tickets.find(t => t.id === ticketId);
      
      if (ticket) {
        // Check if ticket should be shown in sidebar based on filtering logic
        const isUnassigned = !ticket.assignedTo || ticket.assignedTo.trim() === '';
        const { emailLower, displayNameLower } = getCurrentUserIdentifiers();
        const assigneeLower = getAssigneeLower(ticket);
        const isAssignedToCurrentUser = !!currentUser && !!assigneeLower && 
          (assigneeLower === emailLower || (displayNameLower && assigneeLower === displayNameLower));

        const assignmentMatches = doesAssignmentMatch(ticket, { isUnassigned, isAssignedToCurrentUser });

        // Only add if ticket should be shown and not planned
        if (assignmentMatches && !ticket.isPlanned) {
          setTickets(prevTickets => {
            // Check if ticket is already in the list
            const exists = prevTickets.some(t => t.id === ticketId);
            if (!exists) {
              console.log('📋 [TICKET SIDEBAR] Dynamically added ticket back to list:', ticketId);
              return [...prevTickets, ticket];
            }
            return prevTickets;
          });
        }
      }
    } catch (error) {
      console.error('📋 [TICKET SIDEBAR] Error adding ticket back to list:', error);
    }
  };

  // Dynamic ticket addition function for newly created tickets (no API call)
  const addNewTicketToList = (newTicket) => {
    // Determine assignment context
    const isUnassigned = !newTicket.assignedTo || newTicket.assignedTo.trim() === '';
    const { emailLower, displayNameLower } = getCurrentUserIdentifiers();
    const assigneeLower = getAssigneeLower(newTicket);
    const isAssignedToCurrentUser = !!currentUser && !!assigneeLower &&
      (assigneeLower === emailLower || (displayNameLower && assigneeLower === displayNameLower));

    const assignmentMatches = doesAssignmentMatch(newTicket, { isUnassigned, isAssignedToCurrentUser });
    const shouldForceAdd = !!newTicket.__forceVisible;

    // Add if current view matches OR we explicitly force-add, and it's not planned
    if ((assignmentMatches || shouldForceAdd) && !newTicket.isPlanned) {
      setTickets(prevTickets => {
        const exists = prevTickets.some(t => t.id === newTicket.id);
        if (!exists) {
          const enriched = { ...newTicket };
          // In global context, mark as new for assignee so it sorts/prioritizes appropriately
          if (assigneeFilterOptions && newTicket.assignedTo) {
            enriched.isNewTicketForAssignee = true;
            enriched.hasNewActivityForAssignee = true;
          }
          console.log('📋 [TICKET SIDEBAR] Dynamically added newly created ticket to list:', newTicket.id);
          return [...prevTickets, enriched];
        }
        return prevTickets;
      });
    } else {
      console.log('📋 [TICKET SIDEBAR] New ticket not added to sidebar (filtered/planned):', {
        ticketId: newTicket.id,
        isUnassigned,
        isAssignedToCurrentUser,
        isPlanned: newTicket.isPlanned,
        assignmentMatches,
        shouldForceAdd
      });
    }
  };

  // Special function for tickets changed from completed status - respects day-boundary for planned tickets
  const addTicketBackFromCompleted = async (ticketId) => {
    try {
      // Reload the ticket data to get current state
      const response = await apiService.getTickets();
      const ticket = response.tickets.find(t => t.id === ticketId);
      
      if (ticket) {
        // Check if ticket should be shown in sidebar
        const isUnassigned = !ticket.assignedTo || ticket.assignedTo.trim() === '';
        const { emailLower, displayNameLower } = getCurrentUserIdentifiers();
        const assigneeLower = getAssigneeLower(ticket);
        const isAssignedToCurrentUser = !!currentUser && !!assigneeLower && 
          (assigneeLower === emailLower || (displayNameLower && assigneeLower === displayNameLower));
        const isNotCompleted = ticket.status !== 'abgeschlossen';

        // Determine if the ticket matches the selected assignment filter
        const assignmentMatches = doesAssignmentMatch(ticket, { isUnassigned, isAssignedToCurrentUser });

        // Show if: not completed and assignment matches, and either not planned or past day-boundary
        const passesDayBoundary = (!ticket.isPlanned) || isTicketOverdue(ticket);
        if (isNotCompleted && assignmentMatches && passesDayBoundary) {
          setTickets(prevTickets => {
            // Check if ticket is already in the list
            const exists = prevTickets.some(t => t.id === ticketId);
            if (!exists) {
              // Mark ticket with activity flags to show red marker and sort to top
              const ticketWithActivity = {
                ...ticket,
                hasNewActivity: true,  // Show red marker and sort to top
                isNewTicket: false     // It's not a new ticket, but has new activity
              };
              console.log('📋 [TICKET SIDEBAR] Dynamically added ticket back from completed status with activity marker:', ticketId);
              return [...prevTickets, ticketWithActivity];
            }
            return prevTickets;
          });
         } else {
          console.log('📋 [TICKET SIDEBAR] Ticket not added back - fails day-boundary/assignment/completion checks:', {
            ticketId: ticket.id,
            status: ticket.status,
            assignedTo: ticket.assignedTo,
            isNotCompleted,
            isUnassigned,
            isAssignedToCurrentUser,
            isPlanned: ticket.isPlanned,
            isPastDayBoundary: isTicketOverdue(ticket)
          });
        }
      }
    } catch (error) {
      console.error('📋 [TICKET SIDEBAR] Error adding ticket back from completed status:', error);
    }
  };

  // Expose functions to parent
  React.useEffect(() => {
    if (onTicketPlanned) {
      const ticketFunctions = {
        removeTicket: removeTicketFromList,
        addTicket: addTicketToList,
        addNewTicket: addNewTicketToList,  // For newly created tickets (no API call)
        addTicketBackFromCompleted: addTicketBackFromCompleted,  // For tickets changed from completed status
        refreshSidebar: loadUnplannedTickets  // Full refresh (for fallback only)
      };
      onTicketPlanned(ticketFunctions);
    }
  }, [onTicketPlanned, currentUser]);

  // Check if ticket is overdue (day-boundary): show only after the day is over
  const isTicketOverdue = (ticket) => {
    if (!ticket.isPlanned || !ticket.plannedEndDateTime) {
      return false;
    }
    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);
    const endTime = new Date(ticket.plannedEndDateTime);
    return endTime < todayStart;
  };



  // Hilfsfunktion zur Umwandlung der Priorität in eine Zahl für die Sortierung
  const getPriorityOrder = (priority) => {
    switch (priority) {
      case 'kritisch':
        return 1;
      case 'hoch':
        return 2;
      case 'normal':
        return 3;
      case 'niedrig':
        return 4;
      default:
        return 5; // Unbekannte Prioritäten ganz am Ende
    }
  };


  // Filtere ungeplante Tickets UND überfällige Tickets
  const filteredAndSortedTickets = [...tickets]
    .filter(ticket => {
      // Projekt-Planungsmodus: Nur Tickets zum Projekt oder seinen Unterprojekten, nicht abgeschlossen, nicht geplant
      if (projectPlanningFilter) {
        const { projectId, relatedSubIds } = projectPlanningFilter;
        const belongsToProject = (ticket.projectId && Number(ticket.projectId) === Number(projectId)) || (ticket.subprojectId && relatedSubIds && relatedSubIds.has(Number(ticket.subprojectId)));
        const isNotCompleted = ticket.status !== 'abgeschlossen';
        const isUnplanned = !ticket.isPlanned;
        if (!(belongsToProject && isNotCompleted && isUnplanned)) {
          return false;
        }
      }

      // Globale/Einzel-Ansicht: Tickets mit Projekt-/Unterprojektzuordnung i. d. R. nicht anzeigen,
      // außer wenn (a) überfällig & nicht abgeschlossen & dem ausgewählten Nutzer zugeordnet, oder
      // (b) neue Aktivität für den aktuellen Kontext vorliegt, oder (c) __forceVisible (wird unten zusätzlich behandelt)
      if (!projectPlanningFilter && ((ticket.projectId && Number(ticket.projectId) > 0) || (ticket.subprojectId && Number(ticket.subprojectId) > 0))) {
        // Prüfe Sonderfälle
        const computeHasNewForContextFilter = (t) => {
          if (assigneeFilterOptions && selectedAssigneeFilter && selectedAssigneeFilter.type === 'user') {
            return !!(t.isNewTicketForAssignee || t.hasNewActivityForAssignee || t.hasNewEmailsForAssignee || t.hasNewCommentsForAssignee || t.hasNewAttachmentsForAssignee);
          }
          return !!(t.isNewTicket || t.hasNewActivity || t.hasNewEmails || t.hasNewComments || t.hasNewAttachments);
        };

        const { emailLower, displayNameLower } = getCurrentUserIdentifiers();
        const assigneeLowerForSel = getAssigneeLower(ticket);
        const isAssignedToCurrentUserForSel = !!currentUser && !!assigneeLowerForSel &&
          (assigneeLowerForSel === emailLower || (displayNameLower && assigneeLowerForSel === displayNameLower));

        // Bestimme, ob Ticket dem ausgewählten Nutzer (Global-Ansicht) zugeordnet ist
        let isAssignedToSelectedUser = isAssignedToCurrentUserForSel;
        if (!assigneeFilterOptions && viewMode === 'pool') {
          isAssignedToSelectedUser = false;
        }
        if (assigneeFilterOptions && selectedAssigneeFilter) {
          if (selectedAssigneeFilter.type === 'user') {
            const selectedEmailLower = normalizeLower(selectedAssigneeFilter.email);
            const selectedNameLower = normalizeLower(selectedAssigneeFilter.label || selectedAssigneeFilter.displayName);
            isAssignedToSelectedUser = !!assigneeLowerForSel && (
              (selectedEmailLower && assigneeLowerForSel === selectedEmailLower) ||
              (selectedNameLower && assigneeLowerForSel === selectedNameLower)
            );
          } else {
            isAssignedToSelectedUser = false;
          }
        }

        const overdueForSel = isTicketOverdue(ticket);
        const notCompletedForSel = ticket.status !== 'abgeschlossen';
        const hasNewForContext = computeHasNewForContextFilter(ticket);
        const notPlannedForSel = !ticket.isPlanned;

        // __forceVisible wird unten nochmals speziell verarbeitet (mit zusätzlicher Selektion in Global-Ansicht)
        const forceVisible = !!ticket.__forceVisible;

        // Wenn keiner der Sonderfälle greift, blenden wir Projekt-/Unterprojekt-Tickets aus
        if (!(forceVisible || notPlannedForSel || (overdueForSel && notCompletedForSel && isAssignedToSelectedUser) || hasNewForContext)) {
          return false;
        }
      }
      // Force-visibility: allow immediate visibility, but respect assignee toggle in global view
      if (ticket.__forceVisible) {
        if (assigneeFilterOptions && selectedAssigneeFilter) {
          const assigneeLower = getAssigneeLower(ticket);
          if (selectedAssigneeFilter.type === 'user') {
            const selectedEmailLower = normalizeLower(selectedAssigneeFilter.email);
            const selectedNameLower = normalizeLower(selectedAssigneeFilter.label || selectedAssigneeFilter.displayName);
            return !!assigneeLower && (
              (selectedEmailLower && assigneeLower === selectedEmailLower) ||
              (selectedNameLower && assigneeLower === selectedNameLower)
            );
          }
          if (selectedAssigneeFilter.type === 'unassigned') {
            const isUnassigned = !ticket.assignedTo || ticket.assignedTo.trim() === '';
            return isUnassigned;
          }
        }
        return true;
      }
      // Exclude 'wartend' in sidebars
      if (ticket.status === 'wartend') {
        return false;
      }
      // Ticket ist nicht zugewiesen, wenn assignedTo leer, null oder undefined ist
      const isUnassigned = !ticket.assignedTo || ticket.assignedTo.trim() === '';
      
      // Ticket ist dem aktuellen User zugewiesen (case-insensitive, robust)
      const { emailLower, displayNameLower } = getCurrentUserIdentifiers();
      const assigneeLower = getAssigneeLower(ticket);
      const isAssignedToCurrentUser = !!currentUser && !!assigneeLower && 
        (assigneeLower === emailLower || (displayNameLower && assigneeLower === displayNameLower));
      
      // Prüfe ob Ticket überfällig ist (geplant, aber Enddatum in der Vergangenheit)
      const isOverdue = isTicketOverdue(ticket);
      
      // Prüfe ob Ticket nicht abgeschlossen ist
      const isNotCompleted = ticket.status !== 'abgeschlossen';
      
      // Assignment filter depending on selected view mode/unassignedOnly or custom assignee filter options
      const assignmentMatches = doesAssignmentMatch(ticket, { isUnassigned, isAssignedToCurrentUser });

      // Für Überfällige: nur Tickets des aktuellen/ausgewählten Users (nicht unassigned)
      let isAssignedToSelectedUser = isAssignedToCurrentUser;
      if (!assigneeFilterOptions && viewMode === 'pool') {
        isAssignedToSelectedUser = false;
      }
      if (assigneeFilterOptions && selectedAssigneeFilter) {
        if (selectedAssigneeFilter.type === 'user') {
          const selectedEmailLower = normalizeLower(selectedAssigneeFilter.email);
          const selectedNameLower = normalizeLower(selectedAssigneeFilter.label || selectedAssigneeFilter.displayName);
          isAssignedToSelectedUser = !!assigneeLower && (
            (selectedEmailLower && assigneeLower === selectedEmailLower) ||
            (selectedNameLower && assigneeLower === selectedNameLower)
          );
        } else {
          // 'unassigned' or others: treat as not assigned to selected user for overdue logic
          isAssignedToSelectedUser = false;
        }
      }

      // Ein Ticket sollte angezeigt werden, wenn:
      // 1. Es nicht geplant ist (ungeplant) UND assignmentMatches (inkl. unassigned je nach Ansicht)
      // 2. ODER es überfällig ist UND nicht abgeschlossen UND (nur) dem aktuellen/ausgewählten User zugewiesen ist
      let shouldShow = (
        (!ticket.isPlanned && assignmentMatches) ||
        (isOverdue && isNotCompleted && isAssignedToSelectedUser)
      );
      
      // Apply keyword blacklist filtering (current user or selected user in global calendar)
      const effectiveKeywords = (assigneeFilterOptions && selectedAssigneeFilter && selectedAssigneeFilter.type === 'user')
        ? selectedAssigneeTicketFilterKeywords
        : ticketFilterSettings.keywords;
      if (shouldShow && effectiveKeywords && effectiveKeywords.trim()) {
        const keywords = effectiveKeywords
          .split('\n')
          .map(k => k.trim().toLowerCase())
          .filter(k => k.length > 0);
        if (keywords.length > 0) {
          const title = (ticket.title || '').toLowerCase();
          const description = (ticket.description || '').toLowerCase();
          const category = (ticket.category || '').toLowerCase();
          const hasKeyword = keywords.some(keyword =>
            title.includes(keyword) ||
            description.includes(keyword) ||
            category.includes(keyword)
          );
          shouldShow = shouldShow && !hasKeyword;
        }
      }

      // Apply search filter
      if (shouldShow && searchFilter.trim()) {
        // #251: wie bisher Titel, Beschreibung, Kategorie, Nummer -- plus Kunde
        // und Ansprechpartner, über den gemeinsamen Helfer
        shouldShow = ticketPasstZurSuche(ticket, searchFilter, [ticket.description, ticket.category]);
      }
      
      console.log('📋 [TICKET SIDEBAR] Ticket filter check:', {
        ticketNumber: ticket.ticketNumber,
        title: ticket.title,
        assignedTo: ticket.assignedTo,
        isPlanned: ticket.isPlanned,
        isOverdue: isOverdue,
        isNotCompleted: isNotCompleted,
        isUnassigned: isUnassigned,
        isAssignedToCurrentUser: isAssignedToCurrentUser,
        currentUser: currentUser?.username || currentUser?.userPrincipalName || 'none',
        willShow: shouldShow
      });
      
      return shouldShow;
    })
    .sort((a, b) => {
      // 1. Tickets mit roter Ecke (neue Aktivität) haben Vorrang
      const computeHasNewForContext = (t) => {
        if (assigneeFilterOptions && selectedAssigneeFilter && selectedAssigneeFilter.type === 'user') {
          return !!(t.isNewTicketForAssignee || t.hasNewActivityForAssignee || t.hasNewEmailsForAssignee || t.hasNewCommentsForAssignee || t.hasNewAttachmentsForAssignee);
        }
        return !!(t.hasNewActivity || t.hasNewEmails);
      };
      const aHasNewActivity = computeHasNewForContext(a);
      const bHasNewActivity = computeHasNewForContext(b);
      if (aHasNewActivity && !bHasNewActivity) return -1;
      if (!aHasNewActivity && bHasNewActivity) return 1;
      
      // 2. Überfällige Tickets haben hohe Priorität (aber nur außerhalb der roten Ecke)
      if (!aHasNewActivity && !bHasNewActivity) {
        const aIsOverdue = isTicketOverdue(a);
        const bIsOverdue = isTicketOverdue(b);
        if (aIsOverdue && !bIsOverdue) return -1;
        if (!aIsOverdue && bIsOverdue) return 1;
      }
      
      // 3. Nach Priorität (kritisch > hoch > normal > niedrig)
      const priorityA = getPriorityOrder(a.priority);
      const priorityB = getPriorityOrder(b.priority);
      if (priorityA !== priorityB) {
        return priorityA - priorityB;
      }
      
      // 4. Innerhalb gleicher Priorität: Älteste Aktivität zuerst
      const aActivityTime = a.lastActivityAt ? new Date(a.lastActivityAt).getTime() : new Date(a.createdAt || 0).getTime();
      const bActivityTime = b.lastActivityAt ? new Date(b.lastActivityAt).getTime() : new Date(b.createdAt || 0).getTime();
      return aActivityTime - bActivityTime;
    });

  // Compute red-corner indicator for current context
  const hasNewIndicatorForContext = (ticket) => {
    if (assigneeFilterOptions && selectedAssigneeFilter && selectedAssigneeFilter.type === 'user') {
      return !!(ticket.isNewTicketForAssignee || ticket.hasNewActivityForAssignee || ticket.hasNewEmailsForAssignee || ticket.hasNewCommentsForAssignee || ticket.hasNewAttachmentsForAssignee);
    }
    return !!(ticket.hasNewActivity || ticket.hasNewEmails);
  };


  const handleDragStart = (e, ticket) => {
    console.log('📋 [TICKET SIDEBAR] Drag started for ticket:', ticket.ticketNumber);
    
    // Check with parent component if drag is allowed
    let dragAllowed = true;
    if (onTicketDragStart) {
      dragAllowed = onTicketDragStart(ticket);
    }
    
    // If drag is blocked, prevent the operation
    if (dragAllowed === false) {
      e.preventDefault();
      console.log('📋 [TICKET SIDEBAR] Drag prevented by parent component');
      return;
    }
    
    setDraggedTicket(ticket);
    
    // Setze die Drag-Daten
    // Spezialfall: überfällige, noch geplante Tickets aus der Sidebar wie geplante Tickets behandeln,
    // damit im Kalender das Entscheidungs-Modal (Verschieben vs. Weiteren erstellen) erscheint
    const treatAsPlanned = !!ticket.isPlanned && isTicketOverdue(ticket) && ticket.status !== 'abgeschlossen';
    const dragType = treatAsPlanned ? 'planned-ticket' : 'ticket';
    e.dataTransfer.setData('text/plain', JSON.stringify({
      type: dragType,
      ticket: ticket,
      origin: 'sidebar'
    }));
    
    // Setze Drag-Image: vollständig transparentes 1x1 Element (unterbindet OS-Drag-Icon)
    e.dataTransfer.effectAllowed = 'move';
    try {
      const el = document.createElement('div');
      el.style.position = 'fixed';
      el.style.top = '0';
      el.style.left = '0';
      el.style.width = '1px';
      el.style.height = '1px';
      el.style.opacity = '0';
      el.style.pointerEvents = 'none';
      document.body.appendChild(el);
      dragImageElRef.current = el;
      e.dataTransfer.setDragImage(el, 0, 0);
    } catch (_) {}
    
    // Füge visuelle Feedback-Klasse hinzu
    e.target.classList.add('dragging');
  };

  const handleDragEnd = (e) => {
    console.log('📋 [TICKET SIDEBAR] Drag ended');
    setDraggedTicket(null);
    e.target.classList.remove('dragging');
    // Aufräumen: transparentes Drag-Image entfernen
    try {
      if (dragImageElRef.current && dragImageElRef.current.parentNode) {
        dragImageElRef.current.parentNode.removeChild(dragImageElRef.current);
      }
      dragImageElRef.current = null;
    } catch (_) {}
    
    // Benachrichtige parent component
    if (onTicketDragEnd) {
      onTicketDragEnd();
    }
  };

  if (loading) {
    return (
      <div className="ticket-sidebar">
        <div className="sidebar-header">
          <h3>
            <Ticket size={20} style={{ marginRight: '8px' }} />
            {t('ticketSidebar.toPlan', 'Zu planende Tickets')}
          </h3>
        </div>
        <div className="sidebar-loading">
          <RefreshCw className="animate-spin" size={24} />
          <p>{t('ticketSidebar.loadingTickets', 'Lade Tickets...')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="ticket-sidebar">
        <div className="sidebar-header">
          <h3>
            <Ticket size={20} style={{ marginRight: '8px' }} />
            {t('ticketSidebar.toPlan', 'Zu planende Tickets')}
          </h3>
        </div>
        <div className="sidebar-error">
          <AlertCircle size={24} />
          <p>{error}</p>
          <button onClick={handleRefresh} className="btn btn-secondary">
            {t('dashboard.retry', 'Erneut versuchen')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`ticket-sidebar ${isCollapsed ? 'collapsed' : ''}`}>
      {/* Collapse-Button wird in die Sidebar-Actions neben "+ Ticket" verschoben */}
      {isCollapsed && (
        <button
          className="expand-toggle icon-only"
          onClick={() => setIsCollapsed(false)}
          title={t('ticketSidebar.expand', 'Sidebar ausklappen')}
          aria-label={t('ticketSidebar.expand', 'Sidebar ausklappen')}
        >
          <span aria-hidden>{'>'}</span>
        </button>
      )}
      <div className="sidebar-header">
        <h3>
          <Ticket size={20} style={{ marginRight: '8px' }} />
          {t('ticketSidebar.toPlan', 'Zu planende Tickets')}
        </h3>
        <div className="sidebar-actions">
          <button 
            onClick={onCreateTicket}
            className="btn btn-primary btn-sm"
            style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '4px',
              fontSize: '0.875rem',
              padding: '6px 12px'
            }}
            title={t('ticketSidebar.newTicket', 'Neues Ticket erstellen')}
          >
            <Plus size={16} />
            Ticket
          </button>
          {!isCollapsed && (
            <button
              onClick={() => setIsCollapsed(true)}
              className="collapse-toggle icon-only"
              title={t('ticketSidebar.collapse', 'Sidebar einklappen')}
              aria-label={t('ticketSidebar.collapse', 'Sidebar einklappen')}
            >
              <span aria-hidden>{'<'}</span>
            </button>
          )}
        </div>
      </div>

      <div className="sidebar-content">
        {!hideViewToggle && !assigneeFilterOptions && (
          <div className="sidebar-toggle-container">
            <div className="toggle-group">
              <button
                className={`toggle-button ${viewMode === 'mine' ? 'active' : ''}`}
                onClick={() => setViewMode('mine')}
                title={t('ticketSidebar.onlyMineTooltip', 'Nur mir zugewiesene Tickets anzeigen')}
              >
                {t('ticketSidebar.mine', 'Meine')}
              </button>
              <button
                className={`toggle-button ${viewMode === 'pool' ? 'active' : ''}`}
                onClick={() => setViewMode('pool')}
                title={t('ticketSidebar.poolTooltip', 'Nur Pool-Tickets anzeigen')}
              >
                {t('ticketSidebar.pool', 'Pool')}
              </button>
            </div>
          </div>
        )}
        {assigneeFilterOptions && assigneeFilterOptions.length > 0 && (
          <div className="sidebar-toggle-container">
            <div className="toggle-group">
              {assigneeFilterOptions.map((opt, idx) => (
                <button
                  key={`${opt.type}-${opt.email || idx}`}
                  className={`toggle-button ${selectedAssigneeFilter && ((opt.type === selectedAssigneeFilter.type) && (opt.type !== 'user' || (opt.email && selectedAssigneeFilter.email && opt.email.toLowerCase() === selectedAssigneeFilter.email.toLowerCase()))) ? 'active' : ''}`}
                  onClick={() => {
                    setSelectedAssigneeFilter(opt);
                    // Sofortige Sichtbarkeit: Nach Auswahl eines Nutzers die Tickets neu laden,
                    // damit zuvor entplante Tickets sofort erscheinen
                    setTimeout(() => {
                      if (onRefresh) onRefresh();
                    }, 0);
                  }}
                  title={opt.type === 'unassigned' ? 'Nur offene (nicht zugewiesene) Tickets anzeigen' : `Tickets von ${opt.label} anzeigen`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        )}
        <div className="sidebar-search-container">
          <div className="search-input-wrapper">
            <Search size={16} className="search-icon" />
            <input
              type="text"
              placeholder={t('ticketSidebar.searchPlaceholder', 'Tickets durchsuchen...')}
              value={searchFilter}
              onChange={(e) => setSearchFilter(e.target.value)}
              className="sidebar-search-input"
            />
          </div>
        </div>

        <div className="ticket-list">
          {/* #138 (W7): Karte kommt aus ui/TicketCard (4-Zeilen-Layout aus
              #121); Drag & Drop bleibt hier, die Karte reicht nur durch. */}
          {filteredAndSortedTickets.map(ticket => (
            <TicketCard
              key={ticket.id}
              ticket={ticket}
              hasNew={hasNewIndicatorForContext(ticket)}
              enableContextMenu
              onTicketUpdate={() => { try { loadUnplannedTickets(); } catch (_) {} }}
              draggable
              dragging={draggedTicket?.id === ticket.id}
              onDragStart={(e) => handleDragStart(e, ticket)}
              onDragEnd={handleDragEnd}
              onClick={onTicketClick ? (e, tk) => {
                if (draggedTicket?.id === tk.id) return;
                e.preventDefault();
                e.stopPropagation();
                onTicketClick(e, tk);
              } : undefined}
              title={onTicketClick ? (t('ticketDetail.ticket', 'Ticket') + ` #${ticket.ticketNumber}`) : t('header.dropHereNewTicket', 'Hier ablegen für neues Ticket')}
            />
          ))}

          {filteredAndSortedTickets.length === 0 && (
            <div className="no-tickets">
              <Ticket size={48} style={{ opacity: 0.3 }} />
              <p>{searchFilter.trim() ? t('common.noTicketsFound', 'Keine Tickets gefunden') : t('ticketSidebar.noPlanTickets', 'Keine zu planenden Tickets vorhanden')}</p>
              {searchFilter.trim() && (
                <p style={{ fontSize: '0.9rem', color: 'var(--color-text-muted)', marginTop: '8px' }}>
                  Versuchen Sie einen anderen Suchbegriff
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TicketSidebar; 